/**
 * This callback type is called `requestCallback` and is displayed as a global symbol.
 *
 * @callback requestCallback
 */
import React, { Component } from 'react';
import axios from 'axios';
import _ from 'lodash';

import * as localeEn from './utils/locale-en.json';
import { API_URL } from './utils/const';
import { filterGames } from './utils/utils';
import { Container } from './components/common';
import Header from './components/Header/Header';
import Content from './components/Content/Content';
import MobileTooltip from './components/MobileTooltip/MobileTooltip';

class App extends Component {
  state = {
    isMobile: window.innerWidth < 768,
    games: null,
    userData: null,
    error: null,
    loading: false,
    scale: 'week',
    focusDate: new Date(),
    filters: {},
    availableFilters: {},
    translations: localeEn,
    mobileToolip: null
  };

  // add event listener that detect if device is mobile and
  // trigger fetch games request on app mount.
  componentWillMount() {
    window.addEventListener('resize', this.handleWindowSizeChange);

    this.handleFetchGames(null, () => { this.setState({ loading: false }); });
  }

  // remove event listener to prevent memory leak on app unmount.
  componentWillUnmount() { window.removeEventListener('resize', this.handleWindowSizeChange); }

  // if width is less than 768px (isMobile) - change variable in state.
  handleWindowSizeChange = () => { this.setState({ isMobile: window.innerWidth < 768 }); };

  /**
   * Fetch games request.
   *
   * @param {number} year - Requested year (if empty - fetch current year, year before and after).
   * @param {callback} cb - The callback that handles the response.
   * @returns {requestCallback}
   */
  handleFetchGames = (year = undefined, cb = () => undefined) => {
    this.setState({ loading: true });
    const locale = localStorage.getItem('airsoft-user-locale');

    const route = year ? `${API_URL}/game/calendar/${year}` : `${API_URL}/game/calendar`;
    const { games, availableFilters, translations } = this.state;

    return axios.get(route, { params: { locale } })
      .then(({ data }) => (
        this.setState({
          games: { ...games, ...data.games },
          availableFilters: { ...availableFilters, ...data.filter },
          translations: { ...translations, ...data.locale }
        }, () => {
          if (!year) return cb();

          this.setState({ loading: false }, () => cb());
        })
      ))
      .catch(({ response }) => this.setState({
        error: response && response.message ? response.message : 'server is not responding'
      }, () => cb()));
  };

  /**
   * Set kv filter option.
   *
   * @param {string} k - Key of filter.
   * @param {string} v - Value of filter.
   */
  handleChangeFilter = (k, v) => {
    const { filters } = this.state;

    // set lower level of geo filters to null on geo filters change.
    if (k === 'country' && filters.country !== v) {
      return this.setState({ filters: { ...filters, [k]: v, region: null, city: null } });
    } else if (k === 'region' && filters.region !== v) {
      return this.setState({ filters: { ...filters, [k]: v, city: null } });
    }

    this.setState({ filters: { ...filters, [k]: v } });
  }

  /**
   * Change time scale of calendar.
   *
   * @param {string} scale - Needed time scale of calendar.
   * @param {callback} cb - The callback that handles state change.
   * @returns {requestCallback}
   */
  handleChangeScale = (scale, cb = () => undefined) => {
    this.setState({ scale: scale || this.state.scale }, cb());
    if (scale === 'year') {
      const { focusDate, games } = this.state;
      const year = focusDate.getFullYear();
      if (games[year] && games[year][0]) {
        const dateFirstGame = new Date(games[year][0].date_start * 1000);
        if (dateFirstGame.getMonth() !== 0) {
          this.handleFetchGames(year);
        }
      }
    }
  };

  /**
   * Control "direction" of time scroll of user.
   *
   * @param {string} to - "prev" or "next" direction.
   */
  handleManipulationBtnClick = to => {
    const { focusDate, scale, games } = this.state;
    const year = focusDate.getFullYear();
    const month = focusDate.getMonth();
    const date = focusDate.getDate();

    if (to === 'prev' && scale === 'year') {
      return this.setState({ focusDate: new Date(year - 1, month, date) }, () => {
        if (!games[year - 1]) {
          this.handleFetchGames(year - 1);
        }
      });
    } else if (to === 'prev') {
      const updFocusDate = new Date(+focusDate - (1000 * 60 * 60 * 24));
      return this.setState({ focusDate: updFocusDate }, () => {
        if (!games[updFocusDate.getFullYear()]) {
          this.handleFetchGames(updFocusDate.getFullYear());
        } else if (games[updFocusDate.getFullYear()] && games[updFocusDate.getFullYear()][0]) {
          const dateFirstGame = new Date(games[updFocusDate.getFullYear()][0].date_start * 1000);
          const checkDate = new Date();
          let days = 3;
          if (scale === 'month') {
            days = 15;
          }
          checkDate.setTime(updFocusDate.getTime() - (days * 24 * 60 * 60 * 1000));
          if (dateFirstGame.getMonth() > checkDate.getMonth()) {
            this.handleFetchGames(updFocusDate.getFullYear());
          }
        }
      });
    } else if (to === 'next' && scale === 'year') {
      return this.setState({ focusDate: new Date(year + 1, month, date) }, () => {
        if (!games[year + 1]) {
          this.handleFetchGames(year + 1);
        }
      });
    } else if (to === 'next') {
      const updFocusDate = new Date(+focusDate + (1000 * 60 * 60 * 24));
      return this.setState({ focusDate: updFocusDate }, () => {
        if (!games[updFocusDate.getFullYear()]) {
          this.handleFetchGames(updFocusDate.getFullYear());
        }
      });
    }
  };
  // throttle manipulation for desktop scroll of calendar.
  throttleManipulation = _.throttle(to => this.handleManipulationBtnClick(to), 1080);

  /**
   * Change user focus date to needed.
   *
   * @param {date} focusDate
   * @param {string} scale - current or future time scale.
   */
  handleChangeFocusDateTo = (focusDate, scale = 'month') => this.setState({ focusDate, scale });

  /**
   * On scroll event.
   *
   * @param {event} e
   */
  handleScrollInstance = e => {
    const { isMobile, scale, focusDate } = this.state;
    if (isMobile) return;
    e.preventDefault(); e.stopPropagation();

    const delta = Math.abs(e.deltaY) >= Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
    if (scale === 'year') return this.throttleManipulation(delta < 0 ? 'prev' : 'next');

    const inc = scale === 'month' ? 2 : 4;
    const daysFloat = Math.abs(delta) <= 10 ? delta / inc : delta / 100 / inc;
    const date = focusDate.getDate() + parseInt(daysFloat, 10);

    this.setState({ focusDate: new Date(focusDate.getFullYear(), focusDate.getMonth(), date) });
  }

  render() {
    const {
      games, error, filters, focusDate, availableFilters, isMobile, mobileToolip, translations
    } = this.state;
    // show errors in console to not disturb user with popup`s, mb popup needed?
    if (error) console.error(error);

    // count available games to show at header
    const filtersThisYear = !_.isEmpty(availableFilters) &&
      availableFilters[focusDate.getFullYear()] ?
      availableFilters[focusDate.getFullYear()] :
      { address: {}, game_types: [] };
    const filteredGames = filterGames(games, filters);
    const availableGames = !_.isEmpty(filteredGames) &&
      filteredGames[focusDate.getFullYear()] &&
      Array.isArray(filteredGames[focusDate.getFullYear()]) ?
      filteredGames[focusDate.getFullYear()].filter(o => (
        new Date(o.date_end * 1000) > new Date()
      )) :
      [];

    // mobile tooltip functionality: show mobile tooltip against content, if user call it
    if (isMobile && mobileToolip) {
      return (
        <MobileTooltip
          url={mobileToolip.url}
          translations={translations}
          closeMobileTooltip={() => this.setState({ mobileToolip: null })}
        >
          {mobileToolip.content}
        </MobileTooltip>
      );
    }

    return (
      <Container>
        <Header
          {...this.state}
          changeScale={this.handleChangeScale}
          onChangeFilter={this.handleChangeFilter}
          availableFilters={filtersThisYear}
          availableGames={availableGames.length}
        />
        <Content
          {...this.state}
          games={filteredGames}
          changeFocusDateTo={this.handleChangeFocusDateTo}
          onManipulationClick={to => this.handleManipulationBtnClick(to)}
          onScroll={e => this.handleScrollInstance(e)}
          onShowMobileTooltip={(content, url) => this.setState({
            mobileToolip: { content, url }
          })}
          translations={translations}
        />
      </Container>
    );
  }
}

export default App;



// WEBPACK FOOTER //
// ./src/App.js